import { useCallback, useRef, useState } from 'react';

import { FormObject } from '../../@types';
import { HttpMethod } from '../../api/http-client';
import { getNextServiceStatuses, getServiceById, saveServiceStatus } from '../../api/services';
import { BulkRunFailure, BulkRunProgress, runBulk } from '../../utils/bulkRun';

export interface StatusOption {
  code: string;
  name: string;
}

/** Interaction forms captured for a specific job during a status/part-action probe. */
export interface JobInteraction {
  serviceId: number;
  forms: FormObject[];
  originalUrl: string;
  originalPayload: Record<string, unknown>;
  /** HTTP method for the re-submit. Defaults to PUT (status change). POST for part actions. */
  method?: HttpMethod;
}

/**
 * `pending-interactions` — at least one job needs forms, so *nothing* was
 * committed. The caller must not proceed with any dependent work.
 * `committed` — the commit phase ran; `failed` names any job it could not
 * commit, so a partial run can still be reported honestly.
 */
export type JobStatusSubmitOutcome =
  | { state: 'pending-interactions' }
  | { state: 'committed'; succeeded: number[]; failed: BulkRunFailure[] };

export interface UseJobStatusSubmitReturn {
  statusOptions: StatusOption[];
  statusLoading: boolean;
  fetchStatusOptions: (ids: number[]) => Promise<void>;
  resetStatusOptions: () => void;
  pendingInteractions: JobInteraction[];
  clearPendingInteractions: () => void;
  submitStatus: (
    ids: number[],
    statusCode: string,
    notes: string,
    onProgress?: (progress: BulkRunProgress) => void,
    /**
     * Skip the interaction-form probe and commit straight away. Bulk status
     * changes set this: the action is gated on the status-change permission,
     * and holders are expected to understand that variations configured
     * against the jobs are not collected. Halves the requests, too.
     */
    bypassInteractions?: boolean
  ) => Promise<JobStatusSubmitOutcome>;
}

interface StatusType {
  code: string;
  name: string;
}

function intersectStatuses(allSets: StatusType[][]): StatusOption[] {
  if (allSets.length === 0) return [];
  const first = new Map(allSets[0].map((s) => [s.code, s.name]));
  for (let i = 1; i < allSets.length; i++) {
    const codes = new Set(allSets[i].map((s) => s.code));
    for (const code of first.keys()) {
      if (!codes.has(code)) first.delete(code);
    }
  }
  return Array.from(first.entries()).map(([code, name]) => ({ code, name }));
}

/**
 * Status changes for one or many jobs, with the interaction-form probe that
 * makes them safe in bulk.
 *
 * Extracted from `useListJobActions` so the bulk edit dialog can change status
 * through the same path rather than pushing it at the bulk endpoint, which
 * would let jobs skip their required forms.
 *
 * Errors propagate — the caller owns its own submitting/error UI.
 */
export const useJobStatusSubmit = (): UseJobStatusSubmitReturn => {
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [pendingInteractions, setPendingInteractions] = useState<JobInteraction[]>([]);
  // Bulk edit re-fetches whenever its target set changes, so two fetches can be
  // in flight at once. Only the newest may write.
  const fetchSeqRef = useRef(0);

  /** Statuses every selected job can reach — the intersection, not the union. */
  const fetchStatusOptions = useCallback(async (ids: number[]) => {
    const seq = ++fetchSeqRef.current;
    setStatusLoading(true);
    try {
      const allSets = await Promise.all(
        ids.map(async (id) => {
          try {
            const svc = await getServiceById(id);
            const statusCode = svc.serviceStatusCode ?? svc.serviceStatus?.code ?? '';
            const typeCode = svc.serviceTypeCode ?? '';
            const resp = await getNextServiceStatuses(statusCode, typeCode, id);
            return [...(resp.preferedStatuses ?? []), ...(resp.possibleNextStatuses ?? [])];
          } catch {
            return [];
          }
        })
      );
      if (seq !== fetchSeqRef.current) return;
      setStatusOptions(
        ids.length === 1
          ? (allSets[0] ?? []).map((s) => ({ code: s.code, name: s.name }))
          : intersectStatuses(allSets)
      );
    } catch {
      if (seq === fetchSeqRef.current) setStatusOptions([]);
    } finally {
      if (seq === fetchSeqRef.current) setStatusLoading(false);
    }
  }, []);

  const resetStatusOptions = useCallback(() => {
    // Bump the sequence so any in-flight fetch can't repopulate after a reset.
    // That also stops its `finally` clearing the flag, so clear it here — or a
    // reset mid-fetch would leave the dialog stuck on "Loading…".
    fetchSeqRef.current += 1;
    setStatusLoading(false);
    setStatusOptions([]);
    setPendingInteractions([]);
  }, []);

  const clearPendingInteractions = useCallback(() => setPendingInteractions([]), []);

  const submitStatus = useCallback(
    async (
      ids: number[],
      statusCode: string,
      notes: string,
      onProgress?: (progress: BulkRunProgress) => void,
      bypassInteractions = false
    ): Promise<JobStatusSubmitOutcome> => {
      const bypass = {
        nextStatus: statusCode,
        note: notes,
        byPassInteractionCheck: true,
        bulkCheckinData: [],
      };

      // Bulk: commit outright, no probe. Beyond matching the legacy system,
      // this removes the mixed-selection trap — probing first meant one job
      // needing forms held back every other job in the selection, and finishing
      // the forms never came back for them.
      if (bypassInteractions) {
        const result = await runBulk(
          ids,
          (id) =>
            saveServiceStatus(id, bypass as unknown as Parameters<typeof saveServiceStatus>[1]),
          { onProgress }
        );
        return { state: 'committed', succeeded: result.succeeded, failed: result.failed };
      }

      const interactions: JobInteraction[] = [];
      const nonInteractionIds: number[] = [];

      const probePayload = {
        nextStatus: statusCode,
        note: notes,
        byPassInteractionCheck: false,
        bulkCheckinData: [],
      };

      // Phase 1: probe every job. Do NOT commit inline — if any job needs
      // interactions, the user may Cancel, and we don't want some jobs
      // committed while others sit in the interaction dialog.
      //
      // Both phases report through the same progress callback, so a 100-job
      // run shows one continuous count rather than appearing to stall twice.
      const probeResult = await runBulk(
        ids,
        async (id) => {
          const result = (await saveServiceStatus(
            id,
            probePayload as unknown as Parameters<typeof saveServiceStatus>[1]
          )) as unknown as Record<string, unknown>;

          const formData = result.interactionFormData as Array<Record<string, unknown>> | undefined;
          if (!Array.isArray(formData) || formData.length === 0) {
            nonInteractionIds.push(id);
            return;
          }

          const forms = formData.map((obj) => ({
            ...((obj.templateContent ?? obj) as FormObject),
            ruleId: obj.id as number,
            isMandatory: obj.templateIsMandatory as boolean,
            isRepeatable: obj.templateIsRepeatable as boolean,
          })) as FormObject[];

          interactions.push({
            serviceId: id,
            forms,
            originalUrl: `/services/${id}/status`,
            originalPayload: probePayload,
          });
        },
        { onProgress }
      );

      if (interactions.length > 0) {
        // Wait for the user to fill forms or cancel — keep the other jobs pending.
        setPendingInteractions(interactions);
        return { state: 'pending-interactions' };
      }

      // Phase 2: no interactions required anywhere — commit every job.
      const commitResult = await runBulk(
        nonInteractionIds,
        (id) => saveServiceStatus(id, bypass as unknown as Parameters<typeof saveServiceStatus>[1]),
        { onProgress }
      );

      return {
        state: 'committed',
        succeeded: commitResult.succeeded,
        // A job whose probe failed never reached the commit phase, so it is
        // just as unchanged as one that failed to commit.
        failed: [...probeResult.failed, ...commitResult.failed],
      };
    },
    []
  );

  return {
    statusOptions,
    statusLoading,
    fetchStatusOptions,
    resetStatusOptions,
    pendingInteractions,
    clearPendingInteractions,
    submitStatus,
  };
};
